import type { AppApiClient } from '../auth/AppApiClient';
import { t } from '../i18n';
import { LoadingScreenBackgroundSession } from './LoadingScreenBackground';
import type { LoadingScreenSlot } from './LoadingScreenContracts';

export type LoadingScreenMode = 'determinate' | 'indeterminate' | 'completing' | 'revealing' | 'ready' | 'error';

export class LoadingScreen {
  readonly #root: HTMLElement;
  readonly #status: HTMLElement;
  readonly #bar: HTMLElement;
  readonly #progress: HTMLElement;
  readonly #readyButton: HTMLButtonElement;
  readonly #retryButton: HTMLButtonElement;
  #background: LoadingScreenBackgroundSession | null = null;
  #continueHandler: (() => void) | null = null;
  #retryHandler: (() => void) | null = null;
  #hideTimer: number | null = null;
  #completionVersion = 0;
  readonly #animations = new Set<Animation>();

  constructor(private readonly api: Pick<AppApiClient, 'getLoadingScreens' | 'resolveLoadingScreenRendition'>) {
    const root = document.querySelector<HTMLElement>('#app-loading-layer');
    if (!root) throw new Error('Missing #app-loading-layer.');
    this.#root = root;
    root.innerHTML = loadingMarkup();
    this.#status = required(root, '.app-loading__status');
    this.#bar = required(root, '.app-loading__bar');
    this.#progress = required(root, '.app-loading__progress');
    this.#readyButton = required(root, '.app-loading__continue');
    this.#retryButton = required(root, '.app-loading__retry');
    this.#readyButton.addEventListener('click', () => this.#continueHandler?.());
    this.#retryButton.addEventListener('click', () => this.#retryHandler?.());
  }

  show(slot: LoadingScreenSlot): void {
    if (this.#hideTimer !== null) window.clearTimeout(this.#hideTimer);
    this.#hideTimer = null;
    this.#background?.dispose();
    this.setBackground(null);
    this.#continueHandler = null;
    this.#retryHandler = null;
    this.#background = new LoadingScreenBackgroundSession(slot, this.api, this);
    this.#background.start();
    this.#root.dataset.slot = slot;
    this.#root.hidden = false;
    this.#root.setAttribute('aria-hidden', 'false');
    this.#root.classList.remove('app-loading-layer--leaving');
    this.setProgress(0, t('common:status.loading'));
  }

  setProgress(progress: number, label = t('common:status.loading')): void {
    this.#cancelCompletion();
    const normalized = Math.max(0, Math.min(1, progress));
    this.#setMode('determinate');
    this.#status.textContent = label;
    this.#bar.setAttribute('aria-valuenow', String(Math.round(normalized * 100)));
    this.#progress.style.setProperty('--loading-progress', `${normalized * 100}%`);
  }

  setIndeterminate(label = t('common:status.loading')): void {
    this.#cancelCompletion();
    this.#setMode('indeterminate');
    this.#status.textContent = label;
    this.#bar.removeAttribute('aria-valuenow');
  }

  setReady(onContinue: () => void): void {
    this.#cancelCompletion();
    const version = this.#completionVersion;
    const width = getComputedStyle(this.#progress).width;
    this.#setMode('completing');
    this.#bar.setAttribute('aria-valuenow', '100');
    this.#progress.style.setProperty('--loading-progress', '100%');
    this.#status.textContent = t('common:loading.ready');
    this.#readyButton.textContent = t('common:loading.continue');
    void this.#complete(version, width, onContinue);
  }

  async #complete(version: number, width: string, onContinue: () => void): Promise<void> {
    await this.#animate(this.#progress, [{ width }, { width: '100%' }], 300);
    if (version !== this.#completionVersion) return;
    this.#setMode('revealing');
    await Promise.all([
      this.#animate(this.#bar, [{ opacity: 1 }, { opacity: 0 }], 220),
      this.#animate(this.#readyButton, [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }], 220),
    ]);
    if (version !== this.#completionVersion) return;
    this.#continueHandler = onContinue;
    this.#setMode('ready');
  }

  async #animate(element: HTMLElement, frames: Keyframe[], duration: number): Promise<void> {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animation = element.animate(frames, { duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' });
    this.#animations.add(animation);
    try { await animation.finished; }
    catch { /* Replacing or hiding the display cancels its presentation work. */ }
  }

  #cancelCompletion(): void {
    this.#completionVersion += 1;
    this.#continueHandler = null;
    for (const animation of this.#animations) animation.cancel();
    this.#animations.clear();
  }

  setError(message: string, onRetry: () => void): void {
    this.#cancelCompletion();
    this.#retryHandler = onRetry;
    this.#setMode('error');
    this.#status.textContent = message;
    this.#retryButton.textContent = t('common:actions.retry');
    this.#retryButton.focus({ preventScroll: true });
  }

  hide(): void {
    this.#cancelCompletion();
    if (this.#hideTimer !== null) window.clearTimeout(this.#hideTimer);
    this.#continueHandler = null;
    this.#retryHandler = null;
    const background = this.#background;
    this.#root.classList.add('app-loading-layer--leaving');
    this.#root.setAttribute('aria-hidden', 'true');
    const finish = () => {
      this.#hideTimer = null;
      background?.dispose();
      if (this.#background === background) this.#background = null;
      this.#root.hidden = true;
      this.#root.classList.remove('app-loading-layer--leaving');
    };
    if (matchMedia?.('(prefers-reduced-motion: reduce)').matches) finish();
    else this.#hideTimer = window.setTimeout(finish, 180);
  }

  setBackground(url: string | null, focalPoint = { x: 0.5, y: 0.5 }): void {
    if (url) this.#root.style.setProperty('--loading-background', `url("${escapeCssUrl(url)}")`);
    else this.#root.style.removeProperty('--loading-background');
    this.#root.style.setProperty('--loading-focal-x', `${focalPoint.x * 100}%`);
    this.#root.style.setProperty('--loading-focal-y', `${focalPoint.y * 100}%`);
    this.#root.classList.toggle('app-loading-layer--has-background', Boolean(url));
  }

  #setMode(mode: LoadingScreenMode): void {
    this.#root.dataset.mode = mode;
    this.#bar.hidden = mode === 'ready' || mode === 'error';
    this.#readyButton.hidden = mode !== 'ready' && mode !== 'revealing';
    this.#readyButton.disabled = mode !== 'ready';
    this.#retryButton.hidden = mode !== 'error';
    if (mode === 'ready') {
      for (const animation of this.#animations) animation.cancel();
      this.#animations.clear();
    }
  }
}

function loadingMarkup(): string {
  return `<div class="app-loading__background" aria-hidden="true"></div>
    <div class="app-loading__wash" aria-hidden="true"></div>
    <section class="app-loading__panel" role="status" aria-live="polite">
      <p class="app-loading__status">${t('common:status.loading')}</p>
      <div class="app-loading__controls">
      <div class="app-loading__bar" role="progressbar" aria-label="${t('common:status.loading')}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <div class="app-loading__progress"></div>
      </div>
      <button class="app-loading__continue" type="button" hidden></button>
      <button class="app-loading__retry" type="button" hidden></button>
      </div>
    </section>`;
}

function required<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Loading screen element ${selector} is missing.`);
  return element;
}

function escapeCssUrl(value: string): string {
  return value.replace(/["\\\n\r\f]/g, character => `\\${character}`);
}
